'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ZSearchField } from '@/_components/universal/z-search-field'
import { cn } from '@/_lib/utils'
import { buildItem, pushSelectItem, pushViewItemListOnce } from '@/_utils/analytics/datalayer'

import { useOrderFlow, type AmbulanceSelectionPayload } from '../../../_context/order-flow-context'
import { useGetAmbulanceCategories } from '../../../_services/useGetAmbulanceCategories'
import { getOrderThemeClasses } from '../../../_utils/order-flow-section-theme-classes'
import { AOrderLoading } from '../../order-flow/_components/a-order-loading'

import {
	AStepSelectAmbulanceCategoryTabs,
	STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID
} from './_components/a-step-select-ambulance-category-tabs'
import { AStepSelectAmbulanceCategorySections } from './_components/a-step-select-ambulance-group-grid'

import type { IAmbulanceCategory } from '@/_types'

type AStepSelectAmbulanceProps = {
	/**
	 * Called when the user picks a leaf row that has an `IAmbulance` payload — advances the flow to product selection.
	 */
	onAmbulanceSelect: (selection: AmbulanceSelectionPayload) => void
}

/**
 * Order flow step 1: choose an ambulance (clinic) from the hierarchical category API.
 *
 * Top-level categories are tabs with images; search filters visible names within the active tree; clicking a row
 * with `ambulance` invokes {@link AStepSelectAmbulanceProps.onAmbulanceSelect}. Section background follows
 * `sectionTheme` (adult vs child) when provided by the API.
 *
 * @param onAmbulanceSelect - See {@link AStepSelectAmbulanceProps.onAmbulanceSelect}.
 * @returns Loading state, then the themed shell with tabs, search, and grouped ambulance lists.
 */
function flattenAmbulances(categories: IAmbulanceCategory[]): IAmbulanceCategory[] {
	const result: IAmbulanceCategory[] = []
	const traverse = (nodes: IAmbulanceCategory[]) => {
		for (const node of nodes) {
			if (node.ambulance) result.push(node)
			if (node.children?.length) traverse(node.children)
		}
	}
	traverse(categories)
	return result
}

export const AStepSelectAmbulance = ({ onAmbulanceSelect }: AStepSelectAmbulanceProps) => {
	const { orderFlowTopLevelCategoryId } = useOrderFlow()
	const ambulances = useGetAmbulanceCategories()
	const topLevelCategories = useMemo(() => ambulances.data?.data ?? [], [ambulances.data?.data])
	const viewItemListFiredRef = useRef(false)
	const leafNodes = useMemo(() => flattenAmbulances(topLevelCategories), [topLevelCategories])

	const [selectedCategoryId, setSelectedCategoryId] = useState<string | number | null>(null)
	const [searchQuery, setSearchQuery] = useState('')

	useEffect(() => {
		if (viewItemListFiredRef.current || leafNodes.length === 0) return
		const items = leafNodes.map((node, index) =>
			buildItem(
				{ id: node.ambulance!.id, name: node.ambulance!.name, type: 'template', price: 0 },
				{ item_list_id: 'volne-terminy-ambulancie', item_list_name: 'Voľné termíny - výber ambulancie', index }
			)
		)
		const pushed = pushViewItemListOnce('volne-terminy-ambulancie', {
			item_list_id: 'volne-terminy-ambulancie',
			item_list_name: 'Voľné termíny - výber ambulancie',
			items
		})
		if (pushed) viewItemListFiredRef.current = true
	}, [leafNodes])

	// Keep `selectedCategoryId` in sync with API ids; when local state is still `null` after remount
	// (e.g. back from step 2), fall back to `orderFlowTopLevelCategoryId` from the last confirm.
	useEffect(() => {
		if (topLevelCategories.length === 0) return
		setSelectedCategoryId(prev => {
			const validated =
				prev !== null && topLevelCategories.some(c => c.id === prev) ? prev : null
			if (validated !== null) return validated
			if (
				orderFlowTopLevelCategoryId != null &&
				topLevelCategories.some(c => c.id === orderFlowTopLevelCategoryId)
			) {
				return orderFlowTopLevelCategoryId
			}
			return null
		})
	}, [topLevelCategories, orderFlowTopLevelCategoryId])

	const effectiveCategoryId = selectedCategoryId ?? topLevelCategories[0]?.id ?? null

	const activeCategoryTree = useMemo(
		() => topLevelCategories.find(c => c.id === effectiveCategoryId),
		[topLevelCategories, effectiveCategoryId]
	)

	const categorySelectItems = useMemo(
		() =>
			topLevelCategories.map(c => ({
				id: c.id,
				label: c.nameSingular,
				imageUrl: c.image?.url ?? null
			})),
		[topLevelCategories]
	)

	const handleCategorySelect = useCallback((id: string | number) => {
		setSelectedCategoryId(id)
	}, [])

	const handleAmbulanceRowSelect = useCallback(
		(item: IAmbulanceCategory) => {
			if (!item.ambulance || effectiveCategoryId == null || !activeCategoryTree) return
			const index = leafNodes.findIndex(node => node.ambulance?.id === item.ambulance?.id)
			pushSelectItem(
				{ id: item.ambulance.id, name: item.ambulance.name, type: 'template', price: 0 },
				{
					item_list_id: 'volne-terminy-ambulancie',
					item_list_name: 'Voľné termíny - výber ambulancie',
					...(index >= 0 && { index })
				}
			)
			onAmbulanceSelect({
				ambulance: item.ambulance,
				topLevelCategoryId: effectiveCategoryId,
				sectionTheme: activeCategoryTree.sectionTheme ?? null
			})
		},
		[onAmbulanceSelect, effectiveCategoryId, activeCategoryTree, leafNodes]
	)

	if (ambulances.isPending) {
		return <AOrderLoading message='Načítavam ambulancie…' />
	}

	const stepShellTheme = getOrderThemeClasses(activeCategoryTree?.sectionTheme ?? null).surface

	return (
		<div className={cn('rounded-3xl', 'px-4 py-5 md:py-8 md:px-8', stepShellTheme)}>
			<div className='flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-8'>
				<AStepSelectAmbulanceCategoryTabs
					items={categorySelectItems}
					activeId={effectiveCategoryId}
					onSelect={handleCategorySelect}
					className='min-w-0 w-full md:w-auto'
				/>
				<ZSearchField
					value={searchQuery}
					onValueChange={setSearchQuery}
					placeholder='Vyhľadávanie'
					ariaLabel='Vyhľadávanie'
					className='w-full shrink-0 md:max-w-md'
				/>
			</div>

			{activeCategoryTree ? (
				<div
					id={STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID}
					role='tabpanel'
					className='mt-5 lg:mt-9'
				>
					<AStepSelectAmbulanceCategorySections
						activeCategoryTree={activeCategoryTree}
						searchQuery={searchQuery}
						onRowSelect={handleAmbulanceRowSelect}
					/>
				</div>
			) : null}
		</div>
	)
}
